// storageMonitor.js
// Periodically checks how much of the browser's storage quota this app is using,
// and warns the user in the UI if usage is getting dangerously close to the limit.

const WARNING_PERCENT_THRESHOLD = 0.70 // warn once 70% of quota is used
const WARNING_ABSOLUTE_FLOOR_BYTES = 500 * 1024 * 1024 // OR warn if less than ~500MB remains, whichever comes first
const CHECK_THROTTLE_MS = 30 * 1000 // never check more than once every 30 seconds, unless forced
const GENERAL_CHECK_DELAY_MS = 300 // wait this long after a save before checking, to give the background write a moment to finish
const REWARN_INTERVAL_MS = 15 * 60 * 1000 // if still over the threshold, remind the user again every 15 minutes
const ATTACHMENT_CHECK_DELAY_MS = 1000 // wait this long after an attachment save before checking storage, to give the background IndexedDB write time to finish

// TXP-826: the storage warning notification is intentionally unavailable on iOS/iPadOS.
// See checkStorageUsage() below for where this is enforced. Logic mirrors the existing
// iOS check already used (for unrelated PWA-install UX) in src/views/Login.vue.
function isIOS() {
  if (/iPad|iPhone/.test(navigator.userAgent)) return true

  return /Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1
}

let lastCheckedAt = 0
let lastWarnedAt = 0
let latestCheckId = 0
let generalCheckScheduled = false

function buildStorageWarningMessage(usageMb, quotaMb) {
  return `Device storage is running low (${usageMb} MB out of ${quotaMb} MB used). Please sync soon and consider freeing up space on this device to avoid losing unsaved documentation.`
}


// The main function you will call from the rest of the app.
// `store` is the Vuex store instance. `force` skips the 30-second throttle
// (used right after an attachment is added, since that can use a lot of space at once).
export async function checkStorageUsage(store, force = false) {
  if (isIOS()) {
    return // TXP-826: storage warning notification is intentionally disabled on iOS/iPadOS
  }
  
  const now = Date.now()

  if (!force && now - lastCheckedAt < CHECK_THROTTLE_MS) {
    return // too soon since the last check — skip this one to avoid unnecessary overhead
  }
  lastCheckedAt = now

  if (!navigator.storage || !navigator.storage.estimate) {
    return // this browser does not support the Storage API — nothing we can check
  }

  const thisCheckId = ++latestCheckId
  const { quota, usage } = await navigator.storage.estimate()
  if (thisCheckId !== latestCheckId) {
        return // a newer check has already started since this one began — let that newer one decide instead
      }

  if (!quota) {
    return // quota not reported — nothing we can check
  }
  // Some browsers/privacy modes report quota but leave usage undefined; treat as 0
  // to avoid NaN appearing in the warning message.
  const usedBytes = typeof usage === 'number' ? usage : 0
  const remainingBytes = quota - usedBytes
  const percentUsed = usedBytes / quota
  const overThreshold = percentUsed >= WARNING_PERCENT_THRESHOLD || remainingBytes < WARNING_ABSOLUTE_FLOOR_BYTES
  const usageMb = Math.round(usedBytes / 1048576)
  const quotaMb = Math.round(quota / 1048576)

  if (overThreshold) {
    const shouldShowAgain = now - lastWarnedAt > REWARN_INTERVAL_MS
    if (shouldShowAgain) {
      lastWarnedAt = now
      store.commit('setStorageWarning', {
        visible: true,
        message: buildStorageWarningMessage(usageMb, quotaMb),
        color: 'warning',
      })
    }
  } else {
    // usage has dropped back under the threshold (for example, after a sync freed up space) — hide the warning if it was showing
    store.commit('setStorageWarning', { visible: false })
  }
}

// Call this specifically right after an attachment (photo/PDF) is added,
// since that can move storage usage a lot in one step and should not wait for the normal throttle.
export function forceCheckStorageUsage(store) {
  return checkStorageUsage(store, true)
}

// Call this whenever the clinician manually closes the storage warning banner.
   // This tells the check logic "the clinician was just warned," so it won't immediately
   // show the banner again if a check that was already running finishes right afterward.
   export function recordManualDismissal() {
     lastWarnedAt = Date.now()
   }

// Call this after an attachment (photo/PDF) is added, instead of forceCheckStorageUsage directly.
   // This waits a short moment first, since committing the attachment to Vuex does not mean
   // vuex-persist has actually finished writing it to IndexedDB yet — checking too soon could
   // read storage numbers from before the new attachment was actually saved to disk.
   export function forceCheckStorageUsageAfterAttachmentSave(store) {
     setTimeout(() => {
       forceCheckStorageUsage(store)
     }, ATTACHMENT_CHECK_DELAY_MS)
   }

 // Call this after any ordinary save (not an attachment save) to check storage usage.
    // If several saves happen close together, this only schedules one upcoming check for all of them,
    // instead of a separate wait for every single save.
    export function scheduleStorageCheck(store) {
      if (generalCheckScheduled) {
        return // a check is already scheduled to run soon — no need to schedule another one
      }
      generalCheckScheduled = true
      setTimeout(() => {
        generalCheckScheduled = false
        checkStorageUsage(store)
      }, GENERAL_CHECK_DELAY_MS)
    }